import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ModuleGuide } from "./ModuleGuide";
import { Primary, Footer, Stat, Disclaimer, eur } from "./ui";

const Slider = ({ label, value, onChange, max, step, unit, testid }) => (
  <label className="block">
    <span className="flex justify-between text-xs font-medium text-brand-green/70 mb-1">{label}<b className="text-brand-greenDeep">{value} {unit}</b></span>
    <input type="range" min="0" max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#8FA83A]" data-testid={testid} />
  </label>
);

export const applyAssumptions = (monthly, a) => {
  let cum = 0;
  return monthly.map((m) => {
    const revenue = m.revenue * (1 - a.vacancy / 100) * (1 - a.default_rate / 100);
    const expense = m.expense + a.works / 12;
    const net = revenue - expense;
    cum += net;
    return { ...m, revenue, expense, net, cumulative_net: cum };
  });
};

const Chart = ({ rows, onPick, picked }) => {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.cumulative_net)));
  return (
    <div className="flex items-end gap-1 h-40 mt-4 border-b border-brand-soft" data-testid="forecast-chart">
      {rows.map((r, i) => (
        <button key={r.label} onClick={() => onPick(i)} title={`${r.label} : ${eur(r.cumulative_net)}`} className="flex-1 h-full flex flex-col justify-end group" data-testid={`forecast-bar-${i}`}>
          <div className={`w-full rounded-t-md transition-[height] duration-500 ${r.net < 0 ? "bg-red-400" : "bg-brand-limeDark"} ${picked === i ? "ring-2 ring-brand-greenDeep" : "group-hover:opacity-80"}`} style={{ height: `${Math.max(3, (Math.abs(r.cumulative_net) / max) * 100)}%` }} />
          <span className="text-[9px] text-brand-green/50 mt-1 truncate">{r.label.slice(0, 3)}</span>
        </button>
      ))}
    </div>
  );
};

const Sources = ({ sources }) => (
  <div className="text-xs space-y-2 mt-2" data-testid="forecast-sources">
    {[["Loyers", sources.rents], ["Charges récurrentes", sources.expenses], ["Prêts", sources.loans]].map(([t, list]) => list.length > 0 && (
      <div key={t}><p className="font-semibold text-brand-greenDeep">{t}</p>
        {list.map((s, i) => <p key={i} className="text-brand-green/70">{s.label} · {s.property} · {eur(s.amount)} ({s.frequency})</p>)}</div>
    ))}
  </div>
);

const Scenarios = ({ a, setA }) => {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/guides/forecast/scenarios").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/guides/forecast/scenarios", { name, ...a }); setName(""); toast.success("Scénario enregistré"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } };
  return (
    <div className="mt-4" data-testid="forecast-scenarios">
      <div className="flex gap-2"><input className="input flex-1" placeholder="Nom du scénario (ex. prudent)" value={name} onChange={(e) => setName(e.target.value)} data-testid="forecast-scenario-name" />
        <button onClick={save} disabled={!name.trim()} className="inline-flex items-center gap-1 px-4 rounded-full bg-brand-greenDeep text-white text-sm disabled:opacity-40" data-testid="forecast-scenario-save"><Save className="w-4 h-4" /> Enregistrer</button></div>
      <div className="flex flex-wrap gap-2 mt-2">{list.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-brand-softer text-xs">
          <button onClick={() => setA({ vacancy: s.vacancy, works: s.works, default_rate: s.default_rate })} data-testid={`forecast-scenario-${s.id}`}>{s.name}</button>
          <button onClick={async () => { await api.delete(`/guides/forecast/scenarios/${s.id}`); load(); }} className="p-1 text-brand-green/40"><Trash2 className="w-3 h-3" /></button>
        </span>))}</div>
    </div>
  );
};

const Steps = ({ onDone }) => {
  const [d, setD] = useState(null);
  const [a, setA] = useState({ vacancy: 0, works: 0, default_rate: 0 });
  const [picked, setPicked] = useState(null);
  useEffect(() => { api.get("/guides/forecast").then((r) => setD(r.data)); }, []);
  const rows = useMemo(() => (d ? applyAssumptions(d.monthly, a) : []), [d, a]);
  if (!d) return <p className="py-10 text-center text-sm text-brand-green/60">Calcul de votre prévisionnel…</p>;
  const last = rows[rows.length - 1] || { cumulative_net: 0 };
  const neg = rows.filter((r) => r.net < 0).length;
  const pm = picked != null ? rows[picked] : null;
  return (
    <div className="pb-2" data-testid="forecast-steps">
      <div className="flex items-center gap-3">
        <div className="relative w-16 h-16 rounded-full grid place-items-center" style={{ background: `conic-gradient(#8FA83A ${d.reliability * 3.6}deg, #E8EEDC 0)` }}>
          <span className="w-12 h-12 rounded-full bg-white grid place-items-center text-sm font-bold" data-testid="forecast-reliability">{d.reliability}%</span>
        </div>
        <p className="text-sm text-brand-greenDeep">Votre prévisionnel est <b>fiable à {d.reliability} %</b>.</p>
      </div>
      {d.missing.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="forecast-missing">{d.missing.slice(0, 5).map((m, i) => (
          <li key={i}><a href={m.url} className="flex items-center gap-2 text-xs text-amber-800 hover:underline"><AlertTriangle className="w-3.5 h-3.5" />{m.label}</a></li>))}</ul>
      )}
      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <Stat label="Cash-flow cumulé sur 12 mois" value={eur(last.cumulative_net)} tone={last.cumulative_net < 0 ? "text-red-600" : ""} testid="forecast-total" />
        <Stat label="Mois négatifs" value={neg} tone={neg ? "text-red-600" : ""} testid="forecast-negative" />
        <Stat label="Revenus prévus" value={eur(rows.reduce((s, r) => s + r.revenue, 0))} />
      </div>
      <Chart rows={rows} onPick={setPicked} picked={picked} />
      {pm && <div className="mt-3 rounded-2xl bg-brand-softer p-3 text-xs" data-testid="forecast-month-detail"><b>{pm.label}</b> : revenus {eur(pm.revenue)} · dépenses {eur(pm.expense)} · solde {eur(pm.net)}<Sources sources={d.sources} /></div>}
      <div className="grid sm:grid-cols-3 gap-4 mt-5">
        <Slider label="Vacance locative" value={a.vacancy} onChange={(v) => setA({ ...a, vacancy: v })} max={50} step={1} unit="%" testid="forecast-vacancy" />
        <Slider label="Provision travaux" value={a.works} onChange={(v) => setA({ ...a, works: v })} max={10000} step={100} unit="€/an" testid="forecast-works" />
        <Slider label="Taux d'impayés" value={a.default_rate} onChange={(v) => setA({ ...a, default_rate: v })} max={30} step={1} unit="%" testid="forecast-default" />
      </div>
      <Scenarios a={a} setA={setA} />
      <Disclaimer>Estimation indicative à partir de vos baux, charges et prêts. Ne constitue pas un conseil financier.</Disclaimer>
      <Footer><Primary onClick={onDone} data-testid="forecast-done">Terminer</Primary></Footer>
    </div>
  );
};

const Preview = () => (
  <div className="flex items-end gap-1 h-20">{[30, 42, 55, 20, 66, 78, 88, 60, 95, 104, 112, 120].map((h, i) => <div key={i} className={`flex-1 rounded-t ${i === 3 ? "bg-red-300" : "bg-brand-limeDark/70"}`} style={{ height: `${(h / 120) * 100}%` }} />)}</div>
);

export const ForecastGuide = () => (
  <ModuleGuide module="forecast" label="Guide Prévisionnel" intro="Anticipez votre trésorerie des 12 prochains mois."
    benefit="Calculé tout de suite à partir de vos baux, charges et prêts. Ajustez ensuite vos hypothèses." preview={<Preview />} Steps={Steps} />
);
